(() => {
  if (customElements.get('pb30-size-guide')) return;
  const formats = {'30x40':[30,40], '50x70':[50,70], '70x100':[70,100]};
  const formatOf = value => String(value || '').toLowerCase().replace(/cm|\s/g,'').replace(/[×✕]/g,'x');
  class SizeGuide extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.abort = new AbortController();
      try { this.state = JSON.parse(this.querySelector('[data-guide-state]').textContent); } catch { return; }
      this.scope = this.closest('pb15-product');
      this.guidePath = this.querySelector('[data-size-guide]')?.getAttribute('href');
      const variantId = new URLSearchParams(location.search).get('variant');
      const selected = this.state.variants?.find(v => String(v.id) === variantId) || this.state.variants?.find(v => v.id === this.state.selectedVariant) || this.state.variants?.[0];
      this.frame = selected?.options?.[1];
      this.addEventListener('change', event => {
        if (event.target.matches('input[type="radio"]')) this.update();
      }, {signal: this.abort.signal});
      this.addEventListener('click', event => {
        if (event.target.closest('a[aria-disabled="true"]')) event.preventDefault();
      }, {signal: this.abort.signal});
      this.querySelector('[data-guide-apply]')?.addEventListener('click', () => this.apply(), {signal: this.abort.signal});
      this.scope?.addEventListener('change', event => {
        if (event.target.matches('input[data-option="0"]')) this.select(formatOf(event.target.value));
        if (event.target.matches('input[data-option="1"]')) {this.frame = event.target.value; this.update();}
      }, {signal: this.abort.signal});
      this.scope?.addEventListener('pb15:variant-change', event => {
        const variant = event.detail?.variant;
        if (!variant) return;
        this.frame = variant.options?.[1];
        this.select(formatOf(variant.options?.[0]));
      }, {signal: this.abort.signal});
      this.update();
      if (this.hasAttribute('data-full-page')) this.loadContext();
      if (!this.scope && !this.hasAttribute('data-full-page')) {
        const button = this.querySelector('[data-guide-apply]');
        if (button) {button.hidden = true; button.style.display = 'none';}
      }
    }
    disconnectedCallback() { this.abort?.abort(); this.ready = false; }
    select(format) {
      if (!formats[format]) return;
      const input = this.querySelector(`input[value="${format}"]`);
      if (!input) return;
      input.checked = true;
      this.update();
    }
    matchingVariant() {
      return this.state.variants?.find(v => formatOf(v.options?.[0]) === this.format && v.options?.[1] === this.frame);
    }
    setLink(link, url, enabled) {
      if (!link) return;
      link.setAttribute('aria-disabled', enabled ? 'false' : 'true');
      if (enabled && url) link.href = url.pathname + url.search;
      else link.removeAttribute('href');
    }
    renderArtwork() {
      const container = this.querySelector('[data-guide-art-container]');
      const artwork = this.state.artworks?.[this.format];
      container.style.visibility = this.contextPending || this.contextFailed ? 'hidden' : '';
      if (this.contextPending || this.contextFailed) return;
      if (!artwork?.src) {
        const blank = document.createElement('span');
        blank.className = 'pb30-size-guide__blank'; blank.setAttribute('aria-hidden','true');
        container.replaceChildren(blank);
        return;
      }
      const image = this.querySelector('[data-guide-art]') || document.createElement('img');
      image.setAttribute('data-guide-art','');
      image.alt = this.state.title || '';
      image.loading = this.hasAttribute('data-full-page') ? 'eager' : 'lazy';
      image.sizes = '(min-width: 900px) 25vw, 40vw';
      if (artwork.srcset) image.srcset = artwork.srcset;
      else image.removeAttribute('srcset');
      image.src = artwork.src;
      if (artwork.width) image.width = artwork.width;
      if (artwork.height) image.height = artwork.height;
      if (image.parentElement !== container) container.replaceChildren(image);
    }
    update() {
      const format = this.querySelector('input:checked')?.value || '50x70';
      const dims = formats[format];
      if (!dims) return;
      this.format = format;
      this.querySelector('[data-guide-width]').textContent = `${dims[0]} cm`;
      this.querySelector('[data-guide-height]').textContent = `${dims[1]} cm`;
      this.querySelector('[data-guide-room-label]').textContent = `${dims[0]} × ${dims[1]} cm`;
      const currentFrame = this.scope?.querySelector('input[data-option="1"]:checked');
      if (currentFrame) this.frame = currentFrame.value;
      const matching = this.matchingVariant();
      const available = Boolean(matching?.available) && !this.contextPending && !this.contextFailed;
      const status = this.querySelector('[data-guide-status]');
      status.textContent = this.contextPending ? this.state.copy.loading : this.contextFailed ? this.state.copy.error : !available ? this.state.copy.unavailable : '';
      this.renderArtwork();
      const apply = this.querySelector('[data-guide-apply]');
      if (apply) apply.disabled = !available;
      const more = this.querySelector('[data-size-guide]');
      if (more && this.guidePath && this.state.handle) {
        const target = new URL(this.guidePath, location.origin);
        target.searchParams.set('motiv', this.state.handle);
        if (matching) target.searchParams.set('variant', matching.id);
        this.setLink(more, target, available);
      }
      const link = this.querySelector('[data-guide-choose]');
      if (link) {
        const url = this.state.url ? new URL(this.state.url, location.origin) : null;
        if (url && matching) url.searchParams.set('variant', matching.id);
        this.setLink(link, url, available);
      }
    }
    apply() {
      if (!this.scope) return;
      const target = Array.from(this.scope.querySelectorAll('input[data-option="0"]')).find(input => formatOf(input.value) === this.format);
      const status = this.querySelector('[data-guide-status]');
      if (!target || target.disabled || !this.matchingVariant()?.available) { status.textContent = this.state.copy.unavailable; return; }
      target.checked = true;
      target.dispatchEvent(new Event('change', {bubbles:true}));
      status.textContent = this.state.copy.applied;
      this.scope.querySelector('.pb15-pdp__form')?.scrollIntoView({behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block:'center'});
      target.focus({preventScroll:true});
    }
    async loadContext() {
      const params = new URLSearchParams(location.search);
      const handle = params.get('motiv');
      if (!handle) return;
      const variantId = params.get('variant');
      const container = this.querySelector('[data-guide-art-container]');
      this.contextPending = true;
      this.contextFailed = false;
      container.setAttribute('aria-busy','true');
      this.update();
      try {
        if (!/^[a-z0-9][a-z0-9-]{0,200}$/.test(handle)) throw new Error('product');
        const root = new URL(this.dataset.root || '/', location.origin);
        const url = new URL(`products/${encodeURIComponent(handle)}`, root);
        url.searchParams.set('section_id','pb15-product');
        if (variantId && /^\d+$/.test(variantId)) url.searchParams.set('variant', variantId);
        const preview = params.get('preview_theme_id');
        if (preview && /^\d+$/.test(preview)) url.searchParams.set('preview_theme_id', preview);
        const response = await fetch(url, {signal:this.abort.signal});
        if (!response.ok) throw new Error('product');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const raw = html.querySelector('pb30-size-guide [data-guide-state]');
        const state = JSON.parse(raw?.textContent || 'null');
        if (state?.handle !== handle || !Array.isArray(state.variants) || !state.artworks) throw new Error('product');
        if (!Object.values(state.artworks).some(art => art?.src)) throw new Error('image');
        this.state = state;
        this.contextPending = false;
        const selected = state.variants.find(v => String(v.id) === variantId) || state.variants.find(v => v.id === state.selectedVariant) || state.variants.find(v => v.available) || state.variants[0];
        this.frame = selected?.options?.[1];
        if (selected && formats[formatOf(selected.options?.[0])]) this.select(formatOf(selected.options[0]));
        else this.update();
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.contextPending = false;
        this.contextFailed = true;
        this.update();
      } finally {
        container.setAttribute('aria-busy','false');
      }
    }
  }
  customElements.define('pb30-size-guide', SizeGuide);
})();
